import Axios, { AxiosInstance, Method, AxiosError, AxiosRequestConfig } from 'axios'
import qs from 'qs'
import { ClientOptions } from 'src/common-services/types/client-types'
import { API_DEFAULT_TIMEOUT_MS } from 'src/config'

export type queryParams = {
  [key: string]: any
}

export const DEVICE_ID_KEY = 'x-device-id'

export class RequestError extends Error {
  public status: number
  public error?: AxiosError
  public metadata?: any
  constructor(msg: string, status: number, error?: AxiosError, metadata?: any) {
    super(msg)
    this.status = status
    this.error = error
    this.metadata = metadata
  }
}

export const RENDER_TOKEN_NAME = '__am_render_v3'

export const axios = Axios.create({
  timeout: API_DEFAULT_TIMEOUT_MS,
  paramsSerializer(params) {
    return qs.stringify(params)
  },
})

export class BaseClient {
  // public for mocking in storybook
  public readonly axios: AxiosInstance = axios
  private readonly deviceId: string

  constructor(options: ClientOptions) {
    this.deviceId = options.deviceId
  }

  protected async post<T>(url: string, data: any): Promise<T> {
    return this.call<T>(url, 'POST', { data })
  }

  protected async get<T>(url: string, params?: any): Promise<T> {
    return this.call<T>(url, 'GET', { params })
  }

  protected async call<T>(
    url: string,
    method: Method,
    request?: { params?: any; data?: any; responseType?: any; headers?: any; formData?: boolean; timeout?: number }
  ): Promise<T> {
    const headers = {
      [DEVICE_ID_KEY]: this.deviceId,
      ...request?.headers,
    }

    let data = request?.data
    if (request?.formData) {
      headers['Content-Type'] = 'multipart/form-data'
      data = new FormData()
      for (const key in request.data) {
        data.append(key, request.data[key])
      }
    }

    try {
      const req: AxiosRequestConfig = {
        method,
        params: request?.params,
        data,
        responseType: request?.responseType,
        url: `/api${url}`,
        headers,
      }
      if (request?.timeout) {
        req.timeout = request.timeout
      }
      const response = await this.axios.request(req)
      return response.data as T
    } catch (error: any) {
      const message = error.response?.data?.error || error.message
      const metadata = error.response?.data?.metadata
      throw new RequestError(message, error.response?.status, error, metadata)
    }
  }
}
